import type { ReactElement } from "react";

import { useSelector } from "react-redux";

import { Spinner } from "shared-components/decorators/spinner";

import type { Country } from "../../AddressForm/AddressForm.types";
import { selectVatProfilesHaveFetched } from "../../app-main/selectors/fee-profile.selectors";
import { selectFeesHaveFetched } from "../../app-main/selectors/fees.selectors";
import type { TaxDetailsScope } from "../TaxDetails/TaxDetails.types";

import { TaxDetailsForm } from "./TaxDetailsForm/TaxDetailsForm";
import { TaxDetailsFormProvider } from "./TaxDetailsForm/TaxDetailsFormProvider";

type TaxDetailsFormSectionProps = {
  accessToken: string;
  /** Countries in the reader's locale — drives every label they see. */
  allCountries: Country[];
  /** The same countries in English — drives validation. */
  globalCountries: Country[];
  /** Rendered when the URL names a scope we do not serve. */
  pageErrorBlock: () => ReactElement;
  /** Which registration the form is for; `null` for an unrecognised `?scope=`. */
  taxDetailsScope: TaxDetailsScope | null;
};

/**
 * Entry point for the tax details form: resolves what the form is for before
 * handing over to the provider, which owns everything after that.
 */
const TaxDetailsFormSection = ({
  accessToken,
  allCountries,
  globalCountries,
  pageErrorBlock,
  taxDetailsScope,
}: TaxDetailsFormSectionProps) => {
  const haveFeesFetched = useSelector(selectFeesHaveFetched);
  const haveVatProfilesFetched = useSelector(selectVatProfilesHaveFetched);

  if (null === taxDetailsScope) {
    return pageErrorBlock();
  }

  const isLoading =
    !haveFeesFetched || !haveVatProfilesFetched || 0 === allCountries.length;

  if (isLoading) {
    return (
      <div className="spinner-container">
        <Spinner additionalStyle="gray" />
      </div>
    );
  }

  return (
    <TaxDetailsFormProvider
      accessToken={accessToken}
      allCountries={allCountries}
      globalCountries={globalCountries}
      scope={taxDetailsScope}
    >
      <TaxDetailsForm />
    </TaxDetailsFormProvider>
  );
};

export { TaxDetailsFormSection };

export type { TaxDetailsFormSectionProps };
